import type { SequenceDocument, SequenceEvent, Track } from '../format/types';
import { createEmptySequence, createId, createTrack, defaultEventForTrackType } from './defaults';
import type { ProjectAction, ProjectState } from './projectTypes';

export function createDefaultProject(): ProjectState {
  const initial = createEmptySequence('Sequenz 1');
  return { patterns: [initial], activePatternId: initial.id };
}

function updateActivePattern(state: ProjectState, updater: (doc: SequenceDocument) => SequenceDocument): ProjectState {
  return {
    ...state,
    patterns: state.patterns.map((pattern) => (pattern.id === state.activePatternId ? updater(pattern) : pattern)),
  };
}

function updateTrack(doc: SequenceDocument, trackId: string, updater: (track: Track) => Track): SequenceDocument {
  return { ...doc, tracks: doc.tracks.map((track) => (track.id === trackId ? updater(track) : track)) };
}

export function projectReducer(state: ProjectState, action: ProjectAction): ProjectState {
  switch (action.type) {
    case 'LOAD_PROJECT':
      return action.project;

    case 'NEW_PATTERN': {
      const next = createEmptySequence(`Sequenz ${state.patterns.length + 1}`);
      return { patterns: [...state.patterns, next], activePatternId: next.id };
    }

    case 'DUPLICATE_PATTERN': {
      const source = state.patterns.find((pattern) => pattern.id === action.patternId);
      if (!source) return state;
      const copy: SequenceDocument = {
        ...source,
        id: createId('seq'),
        name: `${source.name} Kopie`,
        events: source.events.map((event) => ({ ...event, id: createId('evt') })),
        tracks: source.tracks.map((track) => ({ ...track })),
      };
      return { patterns: [...state.patterns, copy], activePatternId: copy.id };
    }

    case 'RENAME_PATTERN':
      return {
        ...state,
        patterns: state.patterns.map((pattern) => (pattern.id === action.patternId ? { ...pattern, name: action.name } : pattern)),
      };

    case 'DELETE_PATTERN': {
      if (state.patterns.length <= 1) return state;
      const remaining = state.patterns.filter((pattern) => pattern.id !== action.patternId);
      const first = remaining[0];
      const activePatternId = state.activePatternId === action.patternId && first ? first.id : state.activePatternId;
      return { patterns: remaining, activePatternId };
    }

    case 'SELECT_PATTERN':
      return state.patterns.some((pattern) => pattern.id === action.patternId)
        ? { ...state, activePatternId: action.patternId }
        : state;

    case 'SET_BPM':
      return updateActivePattern(state, (doc) => ({ ...doc, timing: { ...doc.timing, bpm: action.bpm } }));

    case 'SET_LENGTH_STEPS':
      return updateActivePattern(state, (doc) => ({
        ...doc,
        timing: { ...doc.timing, lengthSteps: action.lengthSteps },
        events: doc.events.filter((event) => event.step < action.lengthSteps),
      }));

    case 'SET_LOOP':
      return updateActivePattern(state, (doc) => ({ ...doc, timing: { ...doc.timing, loop: action.loop } }));

    case 'SET_SWING':
      return updateActivePattern(state, (doc) => ({ ...doc, timing: { ...doc.timing, swing: action.swing } }));

    case 'ADD_TRACK':
      return updateActivePattern(state, (doc) => ({ ...doc, tracks: [...doc.tracks, createTrack()] }));

    case 'REMOVE_TRACK':
      return updateActivePattern(state, (doc) => ({
        ...doc,
        tracks: doc.tracks.filter((track) => track.id !== action.trackId),
        events: doc.events.filter((event) => event.trackId !== action.trackId),
      }));

    case 'RENAME_TRACK':
      return updateActivePattern(state, (doc) => updateTrack(doc, action.trackId, (track) => ({ ...track, name: action.name })));

    case 'SET_TRACK_TYPE':
      return updateActivePattern(state, (doc) => ({
        ...updateTrack(doc, action.trackId, (track) => ({ ...track, type: action.trackType })),
        events: doc.events.filter((event) => event.trackId !== action.trackId),
      }));

    case 'SET_TRACK_ROLE':
      return updateActivePattern(state, (doc) => updateTrack(doc, action.trackId, (track) => ({ ...track, role: action.role })));

    case 'SET_TRACK_MUTE':
      return updateActivePattern(state, (doc) => updateTrack(doc, action.trackId, (track) => ({ ...track, mute: action.mute })));

    case 'SET_TRACK_SOLO':
      return updateActivePattern(state, (doc) => updateTrack(doc, action.trackId, (track) => ({ ...track, solo: action.solo })));

    case 'SET_TRACK_LEVEL':
      return updateActivePattern(state, (doc) => updateTrack(doc, action.trackId, (track) => ({ ...track, level: action.level })));

    case 'CLEAR_TRACK':
      return updateActivePattern(state, (doc) => ({ ...doc, events: doc.events.filter((event) => event.trackId !== action.trackId) }));

    case 'CLEAR_PATTERN':
      return updateActivePattern(state, (doc) => ({ ...doc, events: [] }));

    case 'TOGGLE_STEP':
      return updateActivePattern(state, (doc) => {
        const track = doc.tracks.find((candidate) => candidate.id === action.trackId);
        if (!track) return doc;
        const existing = doc.events.find((event) => event.trackId === action.trackId && event.step === action.step);
        if (existing) {
          return { ...doc, events: doc.events.filter((event) => event.id !== existing.id) };
        }
        const created = defaultEventForTrackType(track.type, action.trackId, action.step);
        return { ...doc, events: [...doc.events, action.eventId ? { ...created, id: action.eventId } : created] };
      });

    case 'UPDATE_EVENT':
      return updateActivePattern(state, (doc) => ({
        ...doc,
        events: doc.events.map((event) =>
          event.id === action.eventId ? ({ ...event, ...action.patch } as SequenceEvent) : event,
        ),
      }));

    case 'REMOVE_EVENT':
      return updateActivePattern(state, (doc) => ({ ...doc, events: doc.events.filter((event) => event.id !== action.eventId) }));

    case 'REPLACE_ACTIVE_PATTERN':
      return updateActivePattern(state, () => action.document);

    case 'IMPORT_AS_NEW_PATTERN':
      return { patterns: [...state.patterns, action.document], activePatternId: action.document.id };

    default:
      return state;
  }
}
